import { setPointerVars } from '@theme/utils';

/**
 * Header (`<site-header>`, sections/header.liquid), mockup behaviours:
 * - announcement bar: one message every 4 s, paused on hover/focus (`annStep`);
 * - the sticky header is offset by the announcement bar's height (`--ann-h`): the bar scrolls away with the
 *   page, nothing folds (folding it moved the content: jolts while scrolling);
 * - home page: `header.is-stuck` once scrolled past {@link STICK_AT}, back only at the very top (other
 *   templates are rendered stuck);
 * - nav indicator `[data-ind]` sliding under the hovered/focused link;
 * - reading progress `--p` on `[data-bar]`;
 * - cart count bump + accessible label on `cart:updated`;
 * - mobile menu `<dialog>`: ellipse curtain (`.open`), fill from the finger (`mmPt`), Escape, focus kept inside
 *   (modal) and returned to the burger, `aria-expanded`, background scroll locked (CSS `:has`).
 */

/** Duration of the menu closing curtain (base.css: clip-path .8s + delays). */
export const MENU_CLOSE_MS = 900;
export const ANN_INTERVAL_MS = 4000;

/** Scroll offset (px) past which the home header becomes stuck. */
export const STICK_AT = 64;

/**
 * Next sticky state of the home header. Hysteresis: sticking collapses the announcement bar and the browser's
 * scroll anchoring scrolls back by its height, so a single threshold would make the header flip back and
 * forth (shaking bar). Once stuck, it only comes back at the very top.
 *
 * @param {boolean} stuck - Current state.
 * @param {number} y - Scroll offset.
 */
export function nextStuck(stuck, y) {
  return stuck ? y > 0 : y > STICK_AT;
}

/**
 * Marks the messages of an announcement roll from its indexes: `data-on` on the one shown (`data-i`), `data-off`
 * on the one leaving (`data-o`). header.css styles the messages from these marks, whatever their number.
 *
 * @param {HTMLElement} roll - `[data-annroll]`
 */
export function annMark(roll) {
  [...roll.children].forEach((message, k) => {
    message.toggleAttribute('data-on', String(k) === roll.dataset.i);
    message.toggleAttribute('data-off', String(k) === roll.dataset.o);
  });
}

/**
 * Shows the next message of an announcement roll: the current one leaves upwards (`data-o`), the next one comes
 * in from below (`data-i`). The previous outgoing message snaps back without transition (`no-tr`).
 *
 * @param {HTMLElement} roll - `[data-annroll]`
 */
export function annStep(roll) {
  const n = roll.children.length;
  if (n < 2) return;
  const i = Number.parseInt(roll.dataset.i ?? '0', 10) || 0;
  roll.classList.add('no-tr');
  roll.removeAttribute('data-o');
  annMark(roll);
  void roll.offsetWidth;
  roll.classList.remove('no-tr');
  roll.dataset.o = String(i % n);
  roll.dataset.i = String((i + 1) % n);
  annMark(roll);
}

/**
 * Reading progress between 0 and 1.
 *
 * @param {number} scrollTop
 * @param {number} scrollHeight
 * @param {number} clientHeight
 * @returns {number}
 */
export function scrollProgress(scrollTop, scrollHeight, clientHeight) {
  const max = scrollHeight - clientHeight;
  return max > 0 ? Math.min(1, Math.max(0, scrollTop / max)) : 0;
}

/**
 * Picks the singular/plural label for `count` (CLDR rules of `locale`) and fills `{n}`.
 *
 * @param {number} count
 * @param {string} one - e.g. « Panier, {n} article »
 * @param {string} other - e.g. « Panier, {n} articles »
 * @param {string} [locale]
 * @returns {string}
 */
export function pluralLabel(count, one, other, locale) {
  let rule = count === 1 ? 'one' : 'other';
  try {
    rule = new Intl.PluralRules(locale || undefined).select(count);
  } catch {
    // Unknown locale tag: keep the English-like rule.
  }
  return (rule === 'one' ? one : other).replaceAll('{n}', String(count));
}

const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

export class SiteHeader extends HTMLElement {
  /** @type {AbortController | undefined} */
  #listeners;
  /** @type {ReturnType<typeof setInterval> | undefined} */
  #annTimer;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #closeTimer;
  #annPaused = false;
  #raf = 0;
  /** Last cart count shown, so a confirmed optimistic count does not bounce twice. @type {number | undefined} */
  #cartCount;
  /** @type {ResizeObserver | undefined} */
  #annObserver;

  connectedCallback() {
    this.#listeners = new AbortController();
    const signal = this.#listeners.signal;

    // Delegated listeners on the host: children may not be parsed yet.
    this.addEventListener('pointerover', this.#onPointerOver, { signal });
    this.addEventListener('pointerout', this.#onPointerOut, { signal });
    this.addEventListener('pointerdown', this.#onPointerDown, { signal });
    this.addEventListener('focusin', this.#onFocusIn, { signal });
    this.addEventListener('focusout', this.#onFocusOut, { signal });
    this.addEventListener('click', this.#onClick, { signal });
    // `cancel` / `close` do not bubble: catch them on the way down.
    this.addEventListener('cancel', this.#onCancel, { signal, capture: true });
    this.addEventListener('close', this.#onClose, { signal, capture: true });

    document.addEventListener('cart:updated', this.#onCartUpdated, { signal });
    document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
    document.addEventListener('shopify:block:deselect', this.#onBlockDeselect, { signal });
    window.addEventListener('scroll', this.#onScroll, { signal, passive: true });
    window.addEventListener('pageshow', this.#onPageShow, { signal });
    window.matchMedia?.('(width > 960px)').addEventListener('change', this.#onWide, { signal });

    this.#annTimer = setInterval(this.#annTick, ANN_INTERVAL_MS);
    queueMicrotask(() => this.#offsetSticky()); // children may not be parsed yet
    this.#onScroll();
  }

  disconnectedCallback() {
    this.#listeners?.abort();
    clearInterval(this.#annTimer);
    this.#annObserver?.disconnect();
    clearTimeout(this.#closeTimer);
    cancelAnimationFrame(this.#raf);
    this.#raf = 0;
  }

  get header() {
    return this.querySelector('header');
  }

  get dialog() {
    const el = this.querySelector('dialog');
    return el instanceof HTMLDialogElement ? el : null;
  }

  get burger() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-mm-open]'));
  }

  /* ---------- Announcement bar ---------- */

  /** Keeps `--ann-h` (sticky offset of the section wrapper, header.css) at the announcement bar's height. */
  #offsetSticky() {
    const ann = this.querySelector('[data-ann]');
    const wrapper = this.closest('.section-header');
    if (!(ann instanceof HTMLElement) || !(wrapper instanceof HTMLElement) || !('ResizeObserver' in window)) return;
    this.#annObserver = new ResizeObserver(() => wrapper.style.setProperty('--ann-h', `${ann.offsetHeight}px`));
    this.#annObserver.observe(ann);
  }

  #annTick = () => {
    if (this.#annPaused || document.hidden) return;
    for (const roll of this.querySelectorAll('[data-annroll]')) if (roll instanceof HTMLElement) annStep(roll);
  };

  /** @param {Event} event */
  #inAnn(event) {
    return event.target instanceof Element && event.target.closest('[data-ann]') !== null;
  }

  /**
   * @param {Event} event
   * @param {EventTarget | null} related
   */
  #leavesAnn(event, related) {
    return this.#inAnn(event) && !(related instanceof Element && related.closest('[data-ann]'));
  }

  /* ---------- Pointer & focus (delegated) ---------- */

  /** @param {PointerEvent} event */
  #onPointerOver = (event) => {
    if (this.#inAnn(event)) this.#annPaused = true;
    const target = event.target instanceof Element ? event.target : null;
    const link = target?.closest('.gs-nl');
    if (link instanceof HTMLElement) this.#moveIndicator(link);
    const card = target?.closest('.gs-mm-c');
    if (card instanceof HTMLElement) setPointerVars(card, event);
  };

  /** @param {PointerEvent} event */
  #onPointerOut = (event) => {
    if (this.#leavesAnn(event, event.relatedTarget)) this.#annPaused = false;
    const nav = event.target instanceof Element ? event.target.closest('[data-nav]') : null;
    if (nav && !(event.relatedTarget instanceof Node && nav.contains(event.relatedTarget))) this.#hideIndicator();
  };

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    const card = event.target instanceof Element ? event.target.closest('.gs-mm-c') : null;
    if (card instanceof HTMLElement) setPointerVars(card, event);
  };

  /** @param {FocusEvent} event */
  #onFocusIn = (event) => {
    if (this.#inAnn(event)) this.#annPaused = true;
    const link = event.target instanceof Element ? event.target.closest('.gs-nl') : null;
    if (link instanceof HTMLElement) this.#moveIndicator(link);
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    if (this.#leavesAnn(event, event.relatedTarget)) this.#annPaused = false;
    const nav = event.target instanceof Element ? event.target.closest('[data-nav]') : null;
    if (nav && !(event.relatedTarget instanceof Node && nav.contains(event.relatedTarget))) this.#hideIndicator();
  };

  /** @param {HTMLElement} link */
  #moveIndicator(link) {
    const ind = /** @type {HTMLElement | null} */ (this.querySelector('[data-ind]'));
    if (!ind) return;
    ind.style.left = `${link.offsetLeft}px`;
    ind.style.width = `${link.offsetWidth}px`;
    ind.style.opacity = '1';
  }

  #hideIndicator() {
    const ind = /** @type {HTMLElement | null} */ (this.querySelector('[data-ind]'));
    if (ind) ind.style.opacity = '0';
  }

  /* ---------- Reading progress, sticky state (home page) ---------- */

  #onScroll = () => {
    if (this.#raf) return;
    this.#raf = requestAnimationFrame(() => {
      this.#raf = 0;
      const bar = /** @type {HTMLElement | null} */ (this.querySelector('[data-bar]'));
      const el = document.scrollingElement ?? document.documentElement;
      const header = this.header;
      if (this.dataset.stuck === 'scroll' && header)
        header.classList.toggle('is-stuck', nextStuck(header.classList.contains('is-stuck'), el.scrollTop));
      bar?.style.setProperty('--p', scrollProgress(el.scrollTop, el.scrollHeight, el.clientHeight).toFixed(4));
    });
  };

  /* ---------- Cart ---------- */

  /** @param {DocumentEventMap['cart:updated']} event */
  #onCartUpdated = (event) => {
    const count = event.detail.itemCount;
    // An optimistic count confirmed by the server: no second bounce.
    const bump = count !== this.#cartCount;
    this.#cartCount = count;
    for (const cart of this.querySelectorAll('.gs-cart')) {
      if (!(cart instanceof HTMLElement)) continue;
      const { labelOne, labelOther } = cart.dataset;
      if (labelOne && labelOther) {
        cart.setAttribute('aria-label', pluralLabel(count, labelOne, labelOther, document.documentElement.lang));
      }
    }
    for (const badge of this.querySelectorAll('.gs-cnt')) {
      badge.textContent = String(count);
      if (!bump) continue;
      const a = !badge.classList.contains('is-bump-a');
      badge.classList.toggle('is-bump-a', a);
      badge.classList.toggle('is-bump-b', !a);
    }
  };

  /* ---------- Mobile menu ---------- */

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    if (target.closest('[data-mm-open]')) {
      event.preventDefault(); // replaces the native `command="show-modal"` with the animated version
      this.openMenu();
    } else if (target.closest('[data-mm-close]')) {
      event.preventDefault();
      this.closeMenu();
    } else if (target.closest('.gs-mm a[href]')) {
      this.closeMenu();
    }
  };

  /** @param {boolean} open */
  #setCurtain(open) {
    for (const el of this.querySelectorAll('.gs-mm-lead, .gs-mm')) el.classList.toggle('open', open);
    this.burger?.setAttribute('aria-expanded', String(open));
  }

  openMenu() {
    const dialog = this.dialog;
    if (!dialog) return;
    clearTimeout(this.#closeTimer);
    if (!dialog.open) dialog.showModal();
    void dialog.offsetWidth; // start the transitions from the closed state
    this.#setCurtain(true);
    // The panel was `visibility: hidden` when the dialog opened, so `autofocus` could not apply.
    /** @type {HTMLElement | null} */ (this.querySelector('[data-mm-close]'))?.focus({ preventScroll: true });
  }

  /** @param {boolean} [immediate] - Skip the closing curtain. */
  closeMenu(immediate = false) {
    const dialog = this.dialog;
    if (!dialog?.open) return;
    this.#setCurtain(false);
    clearTimeout(this.#closeTimer);
    const done = () => {
      if (dialog.open) dialog.close();
      this.burger?.focus({ preventScroll: true });
    };
    if (immediate || reducedMotion()) done();
    else this.#closeTimer = setTimeout(done, MENU_CLOSE_MS);
  }

  /** Escape: animate instead of the instant native close. @param {Event} event */
  #onCancel = (event) => {
    if (event.target !== this.dialog) return;
    event.preventDefault();
    this.closeMenu();
  };

  /** Closed by the browser anyway (non-cancelable Escape, `command="close"`…): keep the state in sync. */
  #onClose = (/** @type {Event} */ event) => {
    if (event.target !== this.dialog) return;
    clearTimeout(this.#closeTimer);
    this.#setCurtain(false);
  };

  #onWide = (/** @type {MediaQueryListEvent} */ event) => {
    if (event.matches) this.closeMenu(true);
  };

  #onPageShow = (/** @type {PageTransitionEvent} */ event) => {
    if (event.persisted) this.closeMenu(true);
  };

  /* ---------- Theme editor ---------- */

  #onBlockSelect = (/** @type {Event} */ event) => {
    const block = event.target instanceof Element ? event.target : null;
    if (!block || !this.contains(block)) return;
    if (block.closest('.gs-mm')) this.openMenu();
    const roll = block.closest('[data-annroll]');
    if (roll instanceof HTMLElement) {
      this.#annPaused = true;
      roll.removeAttribute('data-o');
      roll.dataset.i = String([...roll.children].indexOf(block));
      annMark(roll);
    }
  };

  #onBlockDeselect = (/** @type {Event} */ event) => {
    const block = event.target instanceof Element ? event.target : null;
    if (!block || !this.contains(block)) return;
    if (block.closest('.gs-mm')) this.closeMenu(true);
    if (block.closest('[data-annroll]')) this.#annPaused = false;
  };
}

if (!customElements.get('site-header')) customElements.define('site-header', SiteHeader);
